// La mesa de un agente en la capa DOM sobre el lienzo: lo que se pulsa (abrir su chat, arrastrarlo, su menú), su
// insignia sobre la cabeza, su etiqueta y la tarjeta al pasar el ratón. Como la pantalla de inicio del iPhone
// (visual.md §2.B-C):
//   · UN solo estilo de etiqueta (oscura, sin relleno de color): el estado se dice con el peso y la luminancia del texto
//     y con un punto solo para «trabajando» y el problema que arregla la oficina.
//   · La urgencia la lleva la insignia sobre la cabeza (24 px, recortada con el fondo), que late dos veces al aparecer.
//   · En «Ordenar» tiembla el monigote entero (muñeco, insignia y etiqueta): el muñeco se pinta aquí, en su lienzo
//     pequeño (Monigote), y el lienzo grande no lo pinta.
//   · Al pasar el ratón (o con el foco): tarjeta opaca con el nombre, la frase completa y «Abrir chat».
// React.memo: un aviso de otro agente no vuelve a pintar esta mesa (Mundo le pasa props estables).
import { MessageCircle, Pause, type LucideIcon } from 'lucide-react';
import { memo, type KeyboardEvent, type PointerEvent as EventoPuntero } from 'react';
import { t } from '../../../i18n';
import { cn } from '../../../lib/utils';
import type { ContratadoDTO } from '../compartido/vocabulario';
import { hace, quien } from '../marco/estado';
import { tituloPuesto } from '../plan/puesto';
import { CELDA, PUESTO, type MesaMundo } from './generar';
import { estiloDe } from './geometria';
import type { MarcadorMundo } from './marcador';
import { MarcaDestino } from './MarcaDestino';
import { MenuAgente, type MoverAGrupo } from './MenuAgente';
import { Monigote } from './Monigote';
import { poseDe } from './pose';

/** Lo que la mesa pide a la escena. Identidad fija (useAccionesEstables): no rompe el memo. */
export interface AccionesMesa {
  abrir: (agentId: string) => void;
  /** pointerdown dentro de la mesa: pulsación larga (menú) y arrastre. */
  pulsar: (e: EventoPuntero, c: ContratadoDTO, indice: number) => void;
  /**
   * Clic en la mesa: abre el chat, salvo que sea el clic de levantar el dedo tras el menú o de soltar un arrastre. Con
   * Ctrl/⌘ (04-11, grupos): elige la mesa en vez de abrirla.
   */
  clic: (agentId: string, mod?: { ctrlKey: boolean; metaKey: boolean }) => void;
  /** Teclado: F2 renombra, la tecla de menú abre el menú; en «Ordenar», flechas = mesa de al lado. */
  teclado: (e: KeyboardEvent<HTMLButtonElement>, c: ContratadoDTO, indice: number) => void;
  abrirMenu: (agentId: string) => void;
  cerrarMenu: () => void;
  /** «Mover de mesa» del menú: abre «Ordenar» (de toda la oficina). */
  ordenar: () => void;
  /** El ratón entra en la mesa o sale de ella (la tarjeta, con su respiro). */
  entrar: (agentId: string) => void;
  salir: () => void;
  enfocar: (agentId: string) => void;
  desenfocar: () => void;
  /** «Abrir chat» de la tarjeta. */
  clicTarjeta: (agentId: string) => void;
  retenerTarjeta: () => void;
  /** Desplazamiento vertical actual de la vista (para poner la tarjeta arriba si cabe). */
  vistaY: () => number;
}

interface Props {
  c: ContratadoDTO;
  m: MarcadorMundo;
  me: MesaMundo;
  z: number;
  sala: string;
  ordenando: boolean;
  /** Va colgando del puntero: su mesa queda medio transparente. */
  arrastrado: boolean;
  /** Es la mesa de destino del arrastre de otro. */
  destino: boolean;
  /** Resaltada desde la lista (pasar el ratón por su fila). */
  resaltado: boolean;
  /** 04-11 — elegida con Ctrl/⌘+clic (para agruparla con otras: Ctrl+G). */
  elegida?: boolean;
  /** 04-11 — «Mover a ▸» de su menú (solo con el menú abierto). */
  menuGrupos?: MoverAGrupo;
  conTarjeta: boolean;
  conMenu: boolean;
  acciones: AccionesMesa;
}

/** La etiqueta (un solo estilo): pastilla oscura que no flota (sin sombra). El tono del texto lo pone quien la usa. */
export const ETIQUETA = 'relative max-w-full rounded-o-s bg-oficina-fondo/90 py-0.5 text-center text-o-nota transition-colors';
/** Los únicos puntos de la escena: trabajando y el problema que arregla la oficina sola (visual.md §2.B.14). */
const PUNTOS = new Set(['bg-oficina-trabajando', 'bg-oficina-situacion-problema']);

export const MesaAgente = memo(function MesaAgente({ c, m, me, z, sala, ordenando, arrastrado, destino, resaltado, elegida = false, menuGrupos, conTarjeta, conMenu, acciones }: Props) {
  const etiqueta = PUESTO.etiqueta * z;
  return (
    <div
      // Sin menú del sistema ni selección de texto al mantener el dedo: la pulsación larga es nuestra.
      className={cn('group absolute select-none [-webkit-touch-callout:none]', ordenando && 'touch-none cursor-grab', arrastrado && 'opacity-40')}
      style={estiloDe(me.rect, z)}
      onPointerDown={(e) => {
        // Lo que va en un portal (el menú del agente) burbujea por React hasta aquí aunque no esté dentro de la mesa:
        // pulsar en una opción y mover el ratón movía al agente de mesa (auditoría B5). Solo cuenta lo de dentro.
        if (!e.currentTarget.contains(e.target as Node)) return;
        acciones.pulsar(e, c, me.indice);
      }}
      onContextMenu={(e) => {
        if (ordenando || !e.currentTarget.contains(e.target as Node)) return;
        e.preventDefault();
        acciones.abrirMenu(c.agentId);
      }}
      onPointerEnter={(e) => e.pointerType === 'mouse' && acciones.entrar(c.agentId)}
      onPointerLeave={(e) => e.pointerType === 'mouse' && acciones.salir()}
    >
      {destino ? <MarcaDestino ocupada /> : null}
      <button
        type="button"
        onClick={(e) => acciones.clic(c.agentId, { ctrlKey: e.ctrlKey, metaKey: e.metaKey })}
        onKeyDown={(e) => acciones.teclado(e, c, me.indice)}
        onFocus={(e) => e.currentTarget.matches(':focus-visible') && acciones.enfocar(c.agentId)}
        onBlur={acciones.desenfocar}
        aria-label={t('oficinaV2.escena.personaje', { nombre: quien(c), puesto: tituloPuesto(c.puesto), sala, estado: m.texto })}
        aria-pressed={elegida || undefined}
        data-agente={c.agentId}
        className={cn('absolute inset-0 rounded-o-m outline-none focus-visible:ring-2 focus-visible:ring-oficina-enlace', resaltado && 'ring-2 ring-oficina-texto2', elegida && 'ring-2 ring-oficina-texto')}
      />
      {/* Lo que tiembla en «Ordenar» (como un icono del iPhone), desfasado una mesa sí y otra no. */}
      <div
        className={cn('pointer-events-none absolute inset-0', ordenando && 'motion-safe:animate-temblor', ordenando && me.indice % 2 === 1 && '[animation-direction:reverse]')}
        data-temblor={ordenando ? true : undefined}
      >
        {ordenando ? (
          <Monigote semilla={c.semillaAvatar || c.agentId} animacion={poseDe(c, !!m.aparcado)} z={z} ancho={CELDA.w} alto={PUESTO.mesa} x={CELDA.w / 2} y={PUESTO.cabeza} className="absolute left-0 top-0" />
        ) : null}
        {m.insignia && m.icono ? <InsigniaAgente key={m.icono.displayName ?? m.insignia} icono={m.icono} clases={m.insignia} top={(PUESTO.cabeza - 7) * z} /> : null}
        <EtiquetaAgente
          texto={m.de ? t('oficinaV2.s12.bandejas.de', { nombre: m.de }) : quien(c)}
          tono={m.de || m.aparcado || m.apagado ? 'ajeno' : m.destaca ? 'te_toca' : 'calma'}
          punto={m.icono || !m.punto || !PUNTOS.has(m.punto) ? null : m.punto}
          resaltada={resaltado}
          top={etiqueta}
        />
      </div>
      {conMenu ? <MenuAgente c={c} alCerrar={acciones.cerrarMenu} alAbrir={() => acciones.abrir(c.agentId)} alMover={acciones.ordenar} aparcado={!!m.aparcado} grupos={menuGrupos} /> : null}
      {conTarjeta && !conMenu ? (
        <TarjetaAgente
          c={c}
          texto={m.texto}
          // Crítica N3: encima de la mesa si cabe en la vista; si no, debajo de la etiqueta.
          posicion={me.rect.y * z - acciones.vistaY() > 150 ? { top: -8, transform: 'translate(-50%, -100%)' } : { top: etiqueta + 40, transform: 'translate(-50%, 0)' }}
          acciones={acciones}
        />
      ) : null}
    </div>
  );
});

/** La insignia sobre la cabeza (como la del iPhone): color sólido, icono oscuro, recortada con el fondo. Late dos veces
 *  al aparecer y se queda quieta (nada late para siempre). */
function InsigniaAgente({ icono: Icono, clases, top }: { icono: LucideIcon; clases: string; top: number }) {
  return (
    <span
      className={cn('pointer-events-none absolute left-1/2 flex h-6 w-6 -translate-x-1/2 -translate-y-full items-center justify-center rounded-full ring-2 ring-oficina-fondo motion-safe:animate-latido', clases)}
      style={{ top }}
      data-testid="insignia"
      aria-hidden
    >
      {/* La pausa, rellena: en contorno se leía como «00». */}
      <Icono className="h-3.5 w-3.5" strokeWidth={2.5} fill={Icono === Pause ? 'currentColor' : 'none'} />
    </span>
  );
}

/**
 * La etiqueta bajo la mesa: hasta 2 líneas (Pau, 06-10: «Analizar rendi…» no dice nada), centrada de verdad (el punto va
 * fuera del flujo). Le toca a Pau: en blanco y seminegrita; en calma: gris; de otra persona: más apagada y dice de quién
 * es (el encargo, en su tarjeta). El hueco entre vecinas sale de `inset-x-2`.
 */
function EtiquetaAgente({ texto, tono, punto, resaltada, top }: { texto: string; tono: 'te_toca' | 'calma' | 'ajeno'; punto: string | null; resaltada: boolean; top: number }) {
  return (
    <span className="pointer-events-none absolute inset-x-2 flex justify-center" style={{ top }}>
      <span
        className={cn(
          ETIQUETA,
          punto ? 'px-4' : 'px-2',
          tono === 'te_toca' ? 'font-semibold text-oficina-texto' : tono === 'calma' ? 'font-medium text-oficina-texto2' : 'font-medium text-oficina-texto3',
          'group-hover:bg-oficina-pulsado',
          resaltada && 'bg-oficina-pulsado',
        )}
        data-tono={tono}
      >
        {punto ? <span className={cn('absolute left-2 top-2 h-1.5 w-1.5 rounded-full', punto)} aria-hidden /> : null}
        <span className="line-clamp-2 text-balance">{texto}</span>
      </span>
    </span>
  );
}

/** La tarjeta al pasar el ratón o con el foco: nombre, cuándo, la frase completa y «Abrir chat». Opaca. */
function TarjetaAgente({ c, texto, posicion, acciones }: { c: ContratadoDTO; texto: string; posicion: { top: number; transform: string }; acciones: AccionesMesa }) {
  return (
    <div
      // Pulsar en la tarjeta («Abrir chat») nunca empieza un arrastre del agente.
      onPointerDown={(e) => e.stopPropagation()}
      className="absolute left-1/2 z-40 w-72 -translate-x-1/2 rounded-o-m bg-oficina-elevada p-3 text-left shadow-o-hoja motion-safe:animate-aparecer"
      style={posicion}
    >
      <p className="flex items-baseline justify-between gap-2">
        <span className="text-o-cuerpo font-semibold text-oficina-texto">{quien(c)}</span>
        <span className="whitespace-nowrap text-o-nota text-oficina-texto3">{hace(c.ultimoInformeEn ?? c.creadoEn)}</span>
      </p>
      <p className="mt-0.5 text-o-sub text-oficina-texto2">{texto}</p>
      <button
        type="button"
        onClick={() => acciones.clicTarjeta(c.agentId)}
        onFocus={acciones.retenerTarjeta}
        // Abrir un chat no es actuar (Pau, 09-10: «el dorado solo para lo que pide que actúes») → botón secundario.
        className="mt-2.5 flex h-9 w-full items-center justify-center gap-2 rounded-o-s bg-oficina-control text-o-texto font-semibold text-oficina-texto outline-none transition-colors duration-150 ease-o-apple hover:bg-oficina-pulsado focus-visible:ring-2 focus-visible:ring-oficina-enlace"
      >
        <MessageCircle className="h-4 w-4" aria-hidden />
        {t('oficinaV2.escena.abrirChat')}
      </button>
    </div>
  );
}
